import React, { useState, useEffect, useCallback } from "react"
import { Plus, Handshake, Pencil } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Badge } from "src/components/ui/badge"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Skeleton } from "src/components/ui/skeleton"
import { useChannelPartners } from "src/features/channel_partners/hooks/useChannelPartners"
import { ChannelPartnerDrawer } from "src/features/channel_partners/components/ChannelPartnerDrawer"
import { Can } from "src/components/shared/Can"

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0)

const STATUS = {
  active: "bg-green-100 text-green-700",
  inactive: "bg-slate-100 text-slate-500",
  blocked: "bg-red-100 text-red-700",
}

function useDebounce(v, d = 300) {
  const [x, setX] = useState(v)
  useEffect(() => {
    const t = setTimeout(() => setX(v), d)
    return () => clearTimeout(t)
  }, [v, d])
  return x
}

export default function ChannelPartnersPage() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [searchRaw, setSearch] = useState("")
  const q = useDebounce(searchRaw)
  const { data, loading, fetch } = useChannelPartners()

  const reload = useCallback(() => fetch({ q }), [fetch, q])
  useEffect(() => { reload() }, [q])

  const partners = data?.channel_partners ?? []
  const openCreate = () => { setEditing(null); setDrawerOpen(true) }
  const openEdit = (cp) => { setEditing(cp); setDrawerOpen(true) }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-bold text-slate-900">Channel Partners</h2>
          <p className="text-[13px] text-slate-500">Brokers selling your projects. Setup a referral code and commission details for each.</p>
        </div>
        <Can resource="channel_partners" action="create">
          <Button size="sm" className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" /> New partner
          </Button>
        </Can>
      </div>

      <div className="max-w-xs">
        <Input placeholder="Search partners…" value={searchRaw} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {loading && !data ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : partners.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-16 text-center">
          <Handshake className="h-10 w-10 text-slate-300" />
          <div>
            <p className="font-medium text-slate-700">{q ? "No partners match your search" : "No channel partners yet"}</p>
            {!q && <p className="text-[13px] text-slate-500">Add a broker to issue a referral code and track commissions.</p>}
          </div>
          {!q && (
            <Can resource="channel_partners" action="create">
              <Button size="sm" className="gap-2" onClick={openCreate}>
                <Plus className="h-4 w-4" /> New partner
              </Button>
            </Can>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead className="font-semibold text-black">Partner</TableHead>
                <TableHead className="font-semibold text-black">Status</TableHead>
                <TableHead className="font-semibold text-black">Referral Code</TableHead>
                <TableHead className="font-semibold text-black">Commission</TableHead>
                <TableHead className="text-right font-semibold text-black">Leads</TableHead>
                <TableHead className="text-right font-semibold text-black">Closings</TableHead>
                <TableHead className="text-right font-semibold text-black">Commission (paid / pending)</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.map((cp, idx) => (
                <TableRow key={cp.id} className="group cursor-pointer" onClick={() => openEdit(cp)}>
                  <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                  <TableCell>
                    <div className="text-[13px] font-medium text-slate-900">{cp.name}</div>
                    <div className="text-[12px] text-slate-400">{cp.company_name || cp.email || "—"}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${STATUS[cp.status]} border-0 capitalize`}>{cp.status}</Badge>
                  </TableCell>
                  <TableCell>
                    {cp.referral_code ? (
                      <Badge className="border-0 bg-[#ecebff] font-mono text-[#4f39f6]">{cp.referral_code}</Badge>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-[13px] text-slate-600">
                    {cp.commission_value != null ? (
                      cp.commission_type === "percent" ? `${cp.commission_value}%` : inr(cp.commission_value)
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-right text-[13px] text-slate-600">{cp.leads_count ?? 0}</TableCell>
                  <TableCell className="text-right text-[13px] text-slate-600">{cp.closings_count ?? 0}</TableCell>
                  <TableCell className="text-right text-[13px] text-slate-700">
                    {inr(cp.commission_paid)} <span className="text-slate-300">/</span>{" "}
                    <span className="text-amber-600">{inr(cp.commission_pending)}</span>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-0.5">
                      <Can resource="channel_partners" action="update">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(cp)} title="Edit">
                          <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                        </Button>
                      </Can>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ChannelPartnerDrawer open={drawerOpen} partner={editing} onClose={() => setDrawerOpen(false)} onSuccess={reload} />
    </div>
  )
}
